import { maybe, task, type Maybe, type Task } from 'true-myth';
import { getClient } from '@zg-rentals/http-client';
import { ChainedError } from '@zg-rentals/stdlib/task';

// Temporary source for the user's zuid until a supported one exists (RPF-134): page frame's own client-data
// endpoint, which PFS doesn't publish as a stable contract, so keep every use of it in this file
const CLIENT_DATA_PATH = 'pfs/api/v1/client-data';

const ENCODED_ZUID_PREFIX = 'X1-';

type ClientData = { user?: { encodedZuid?: unknown } } | null;

const toEncodedZuid = (value: unknown): Maybe<string> =>
  typeof value === 'string' && value.startsWith(ENCODED_ZUID_PREFIX) ? maybe.just(value) : maybe.nothing<string>();

/**
 * Looks up the signed-in user's encoded zuid from page frame's client-data endpoint: Just when signed in,
 * Nothing when logged out, rejected when the lookup itself fails
 */
export const fetchEncodedZuid = (): Task<Maybe<string>, ChainedError> =>
  task.tryOrElse(
    (cause) => ChainedError.of('client-data lookup failed', cause),
    async () => {
      // page frame serves client-data on the page's own origin, whatever prefixUrl the app's client uses
      const data = await getClient()
        .get(CLIENT_DATA_PATH, { prefixUrl: window.location.origin, retry: 0 })
        .json<ClientData>();

      return toEncodedZuid(data?.user?.encodedZuid);
    },
  );
